import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, effect, inject, input, OnInit, signal, untracked, WritableSignal, ViewChildren, QueryList, AfterViewChecked, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  ComparisonType,
  DatePickerComponent,
  DateRangePickerOutput,
} from '../../../../shared/components/date-picker/date-picker.component';
import { ApiService } from '../../../../core/services/api.service';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import {
  IconComponent,
  IconName,
} from '../../../../shared/icons/icon.component';
import { LineChartComponent } from '../../../../shared/components/charts/line-chart/line-chart.component';
import { ToggleBtnsComponent } from '../../../../shared/components/toggle-btns/toggle-btns.component';
import { MultipleBarComponent } from '../../../../shared/components/charts/bar-charts/multiple-bar/multiple-bar.component';
import { UnitSuggestionService } from '../../../../core/services/unit-suggestion.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import {
  ScrollTableComponent,
  TableHeader,
} from '../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { DashboardCardsComponent } from '../../../../shared/components/ui/dashboard-cards/dashboard-cards.component';
import { MatDialogModule } from '@angular/material/dialog';
import {
  DashboardPageEngine,
  type DashboardEngineConfig,
} from '../../../../core/services/dashboard-page-engine';
import {
  MonthlyReport2SectionId,
  MONTHLY_REPORT_2_CARD_CONFIG,
  isMonthlyReport2CardId,
  type DashboardCardLayoutItem,
} from '../../../../core/enums/dashboard-cards.enum';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { environment } from '../../../../../environments/environment';
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MobileSheetComponent, MobileSheetData } from '../../../../shared/components/mobile-sheet/mobile-sheet.component';
import { CommonService } from '../../../../core/services/common.service';
import { BreakpointService } from '../../../../core/services/breakpoint.service';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { AiHeadlinesComponent } from '../../../../shared/components/ai-headlines/ai-headlines.component';
import { MonthlyReport2MobileComponent } from './mobile-view/monthly-report-2-mobile.component';

dayjs.extend(utc);
dayjs.extend(timezone);
const LA_TIMEZONE = 'America/Los_Angeles';

/** Draft row used by the inline Customize Layout editor (mini-dashboard preview). */
interface LayoutEditorItem {
  id: MonthlyReport2SectionId;
  title: string;
  visible: boolean;
  /** 4 | 6 | 8 | 12 — proportional width inside the 12-col mini grid. */
  colSpan: number;
  order: number;
  widgetId: number;
  placementId?: number;
  sourcePageId: number | null;
  customConfig: Record<string, any> | null;
}

@Component({
  standalone: true,
  selector: 'app-monthly-report-2',
  imports: [
    CommonModule,
    DragDropModule,
    DatePickerComponent,
    IconComponent,
    LineChartComponent,
    ToggleBtnsComponent,
    MultipleBarComponent,
    ScrollTableComponent,
    IndividualToasterComponent,
    DashboardCardsComponent,
    MatDialogModule,
    AiHeadlinesComponent,
    MatBottomSheetModule,
    // IconComponentV2,
    IconComponentV2,
    MonthlyReport2MobileComponent,
  ],
  templateUrl: './monthly-report-2.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DatePipe],
})
export class MonthlyReport2Component extends DashboardPageEngine<MonthlyReport2SectionId> implements OnInit, AfterViewChecked {
  // ─── Engine config ────────────────────────────────────────────────────────
  /** Backend pages.id for Monthly Report 2 */
  protected readonly dashboardEngineConfig: DashboardEngineConfig<MonthlyReport2SectionId> = {
    pageId: 2,
    cardConfig: MONTHLY_REPORT_2_CARD_CONFIG,
    isCardId: isMonthlyReport2CardId,
  };

  // ─── Component services ───────────────────────────────────────────────────
  private apiService = inject(ApiService);
  private route = inject(ActivatedRoute);
  private datePipe = inject(DatePipe);
  private unitService = inject(UnitSuggestionService);
  private productConfigService = inject(ProductConfigService);
  private _bottomSheet = inject(MatBottomSheet);
  public _commonService = inject(CommonService);
  public _breakpointService = inject(BreakpointService);
  private customDashboardPin = inject(CustomDashboardPinService);

  /** Expose section-ID enum to the template unchanged. */
  readonly SectionId = MonthlyReport2SectionId;

  @ViewChildren(MultipleBarComponent) multipleBars!: QueryList<MultipleBarComponent>;

  ngAfterViewChecked() {
    this.multipleBars?.forEach(chart => {
      if (!(chart as any)._patchedScroll) {
        const original = chart['has5OrMoreYearsOfData'].bind(chart);
        chart['has5OrMoreYearsOfData'] = () => {
          const len = Math.max(
            chart.data1?.length || 0,
            chart.data2?.length || 0,
            chart.data3?.length || 0,
            chart.data4?.length || 0
          );
          if (len > 20) return true;
          return original();
        };
        (chart as any)._patchedScroll = true;
        
        setTimeout(() => {
          if (typeof (chart as any).renderChart === 'function') {
            (chart as any).renderChart();
          }
        }, 0);
      }
    });
  }

  /**
   * When true, this component is being embedded inside another page (e.g.
   * My Dashboard) and should hide its page chrome (date-picker, hidden-
   * section menu, Customize Layout button). Mirrors the same `[mixedMode]`
   * pattern used by `MonthlyReport1Component`.
   */
  readonly mixedMode = input<boolean>(false);

  /**
   * Optional allow-list of backend widget keys (e.g. `mr2_stats_overview`)
   * or canonical section ids (same value as the enum). When set, the
   * template's `@for` over `filteredDashboardCards()` renders ONLY the
   * cards in this list — everything else is filtered out via the same
   * `@switch` block, so the parent (My Dashboard) doesn't need to
   * re-implement per-card rendering.
   *
   * Pass `null` (default) for the standalone Monthly Report 2 page so the
   * full layout from `DashboardPageEngine.orderedDashboardCards()` renders
   * unchanged.
   */
  readonly visibleWidgetKeys = input<string[] | null>(null);

  /**
   * `orderedDashboardCards()` filtered down to the subset requested by the
   * parent via `[visibleWidgetKeys]`. Backend `widget_key` values are
   * already identical to the `MonthlyReport2SectionId` enum values
   * (`mr2_*`), so we can match directly.
   */
  readonly filteredDashboardCards = computed(() => {
    const keys = this.visibleWidgetKeys();
    const all = this.mixedMode()
      ? this.allAllowedDashboardCards()
      : this.orderedDashboardCards();
    if (!keys || keys.length === 0) return all;

    const allowed = new Set<string>();
    for (const key of keys) {
      if (isMonthlyReport2CardId(key)) allowed.add(key);
    }
    if (allowed.size === 0) return all;

    return all.filter((c) => allowed.has(c.id));
  });

  /**
   * Cards the host of this report is currently allowed to render. When
   * standalone (`mixedMode() === false`) all cards are active. When
   * embedded by `WidgetHostComponent` (My Dashboard), only the cards
   * matching `visibleWidgetKeys` are active — used to skip fan-out API
   * calls for cards the host never renders.
   *
   * IMPORTANT: derived directly from `visibleWidgetKeys()` (synchronous
   * host input), NOT from `filteredDashboardCards()` — the latter depends
   * on the personalized layout (`orderedDashboardCards`) which is loaded
   * asynchronously and is empty at the moment the productId effect fires
   * `fetchAllReports()`.
   */
  private readonly activeCardIds = computed<ReadonlySet<MonthlyReport2SectionId>>(() => {
    const keys = this.visibleWidgetKeys();
    if (!keys || keys.length === 0) return new Set();
    const allowed = new Set<MonthlyReport2SectionId>();
    for (const key of keys) {
      if (!key) continue;
      if (isMonthlyReport2CardId(key)) {
        allowed.add(key);
        continue;
      }
      // MR2 backend keys are already `mr2_*` (canonical) for Denefits and
      // product-prefixed for other products (e.g. `cr_mr2_*`). Strip the
      // 2-char product prefix and try again.
      const m = /^[a-z]{2}_(mr2_.+)$/i.exec(key);
      if (m && isMonthlyReport2CardId(m[1])) {
        allowed.add(m[1] as MonthlyReport2SectionId);
      }
    }
    return allowed;
  });

  private isSectionActive(...sectionIds: MonthlyReport2SectionId[]): boolean {
    if (!this.mixedMode()) return true;
    const active = this.activeCardIds();
    if (active.size === 0) return true;
    return sectionIds.some((id) => active.has(id));
  }

  /**
   * Local UI state for the "Add to Custom Dashboard" quick action on each card.
   * Tracks cards already pushed to the user's mixed dashboard in this session
   * and which card (if any) is currently in-flight, so the button can show
   * the correct loading / success state.
   */
  readonly addedToCustomDashboard = signal<Set<MonthlyReport2SectionId>>(new Set());
  readonly addingToCustomDashboardId = signal<MonthlyReport2SectionId | null>(null);

  /**
   * Tracks the productId we have already hydrated `addedToCustomDashboard`
   * for, so the follow-up `mixed=true&product_id=<id>` request fires exactly
   * once per product per page-load (no duplicate network hits).
   */
  private _hydratedCustomDashboardForProductId: string | number | null = null;

  selectedProductId = signal<string | null>(null);
  /** Tags in-flight report fetches so stale responses are ignored after product changes. */
  private reportFetchGeneration = 0;
  // startDate = signal<string | null>(null);
  // endDate = signal<string | null>(null);
  // ─── Mobile responsive support (merged from manisha-mobile_responsive) ───
  startDate = signal<string | null>(
    dayjs.utc().tz(LA_TIMEZONE).subtract(6, 'day').startOf('day').format('YYYY-MM-DD')
  );
  endDate = signal<string | null>(
    dayjs.utc().tz(LA_TIMEZONE).endOf('day').format('YYYY-MM-DD')
  );

  // Always-current selected range tracker, kept independent of the
  // `startDate`/`endDate` signals above (which the existing comparison
  // flow temporarily flips to the *previous* period via `isPrevious`).
  // These are what the mobile filter sheet passes back into the picker
  // on re-open so the previously applied range shows up as selected,
  // mirroring the behavior of monthly-report-1's mobile flow.
  currentStartDate = signal<string | null>(
    dayjs.utc().tz(LA_TIMEZONE).subtract(6, 'day').startOf('day').format('YYYY-MM-DD')
  );
  currentEndDate = signal<string | null>(
    dayjs.utc().tz(LA_TIMEZONE).endOf('day').format('YYYY-MM-DD')
  );
  comparisonType = signal<ComparisonType>('none');
  // Seed with the label matching the default `startDate`/`endDate` above
  // ("Last 7 Days") so the picker's preset trigger doesn't fall back to
  // "Tap to Select" on first open (matches monthly-report-1).
  presetLabel = signal<string | undefined>('Last 7 Days');
  sectionLoading = signal<Record<string, boolean>>({});

  isSectionLoading(section: string): boolean {
    return this.sectionLoading()[section] || false;
  }

  setSectionLoading(section: string, isLoading: boolean): void {
    this.sectionLoading.update(prev => ({ ...prev, [section]: isLoading }));
  }

  dateRangePayloads = signal<{ payload: any; previousPayload: any } | null>(null);

  constructor() {
    super(); // initialises DashboardPageEngine signals + saving$ subscription

    // Seed the plain `start_date` / `end_date` props and `dateRangePayloads`
    // signal from the default `startDate()` / `endDate()` signal values so
    // the product-selection effect fires the initial fetch immediately,
    // without waiting for the desktop date-picker's `ngOnInit` emission to
    // land. On mobile the desktop section is hidden via Tailwind `hidden`
    // (display: none) but still mounted, so the date-picker eventually emits
    // — but that previously left a visible window where mobile KPIs
    // (`Enrolled Businesses`, `Contracts Created`, `Activation Rate`,
    // training stats, MoM charts, running totals, etc.) all rendered as
    // empty/zero placeholders.
    this.seedInitialDateRangePayloads();
    // Mirror the `[mixedMode]` input onto the engine's `isMixedDashboard`
    // signal so `loadPersonalizedDashboardLayout()` (inherited) hits the
    // `mixed=true&product_id=<id>` URL when this page is embedded inside
    // My Dashboard. Mirrors the same effect used by MR1.
    effect(() => {
      const mixed = this.mixedMode();
      untracked(() => {
        if (this.isMixedDashboard() !== mixed) {
          this.isMixedDashboard.set(mixed);
        }
      });
    });

    effect(() => {
      const globalProductId = this.apiService.selectedProductId();
      const payloads = this.dateRangePayloads();

      if (globalProductId) {
        untracked(() => {
          if (this.selectedProductId() !== globalProductId) {
            this.reportFetchGeneration += 1;
          }
          this.selectedProductId.set(globalProductId);

          // CRITICAL: sync `isMixedDashboard` from the input value *before*
          // calling `loadPersonalizedDashboardLayout()` so embedded mode
          // (`<app-monthly-report-2 [mixedMode]="true">`) goes straight to
          // the `mixed=true&product_id=<id>` URL on the first load.
          const isMixed = this.mixedMode();
          if (this.isMixedDashboard() !== isMixed) {
            this.isMixedDashboard.set(isMixed);
          }

          // Always pre-seed `cachedProductId` from the sidebar's
          // currently-selected product BEFORE we kick off any layout
          // load. Doing this unconditionally (not just when `isMixed`
          // is true) protects the standalone-route case too: if a
          // non-mixed response writes the wrong `product_id` into
          // `cachedProductId`, the next call into the override will
          // still re-sync, but at least our initial value is correct
          // and the engine's bootstrap path can never fire with
          // `cachedProductId == null`. See full rationale in MR1.
          this._syncCachedProductIdToSelectedProduct(globalProductId);

          this.loadPersonalizedDashboardLayout(); // overridden below

          // When embedded inside My Dashboard the `<app-date-picker>` is
          // hidden (see template `@if (!mixedMode())`), so `onDateRangeChange`
          // never fires and `dateRangePayloads` stays null — which means
          // `fetchAllReports` would never run and every chart/table would
          // stay empty. Seed a default last-30-days window here so the
          // embedded copy renders data immediately. We call
          // `fetchAllReports` directly (instead of writing to
          // `dateRangePayloads`) to avoid re-triggering this effect and
          // double-firing `loadPersonalizedDashboardLayout`.
          if (isMixed) {
            this.ensureDefaultDateRangeForMixedMode();
          }
        });
      }

      if (globalProductId && payloads) {
        untracked(() => {
          this.fetchAllReports(payloads.payload, payloads.previousPayload);
        });
      }
    });
 
    effect(() => {
      const pid = this.cachedProductId();
      if (pid == null) return;
      if (this._hydratedCustomDashboardForProductId === pid) return;
      // Skip the rehydration call when this report is embedded — the
      // "Pin to Dashboard" badge isn't shown in mixed mode anyway.
      if (this.mixedMode()) return;

      untracked(() => { 
        const selectedCuid = this.apiService.selectedProductId();
        const expectedPid = this.resolveActiveProductPid(selectedCuid);
        if (
          expectedPid != null &&
          String(pid) !== String(expectedPid)
        ) { 
          return;
        }

        this._hydratedCustomDashboardForProductId = pid;
        this.addedToCustomDashboard.set(new Set());
        this.hydrateAddedToCustomDashboardFromServer(pid);
      });
    });
 
    effect(() => {
      const loading = this.layoutLoading();
      const pendingCuid = this._pendingReloadForCuid();

      if (!loading && pendingCuid != null) {
        untracked(() => {
          this._pendingReloadForCuid.set(null);
          this.loadPersonalizedDashboardLayout();
        });
      }
    });
 
    effect(() => {
      const cached = this.cachedProductId();
      const selectedCuid = this.apiService.selectedProductId();
      if (selectedCuid == null) return;

      untracked(() => {
        const expected = this.resolveActiveProductPid(selectedCuid);
        if (expected == null) return;
        if (String(cached ?? '') === String(expected)) return;
 
        this.cachedProductId.set(expected);
      });
    });
  }
 
  private _pendingReloadForCuid = signal<string | number | null>(null);
 
  private resolveActiveProductPid(
    selectedCuid: string | number | null,
  ): string | number | null {
    const isNumericPid = (v: unknown): boolean =>
      v != null && /^\d+$/.test(String(v));

    if (selectedCuid == null) return null;

    // 1) dynamic_routes (sessionStorage) — sidebar's authoritative source
    try {
      const raw = sessionStorage.getItem('dynamic_routes');
      if (raw) {
        const parsed = JSON.parse(raw);
        const routes = Array.isArray(parsed) ? parsed : parsed?.routes;
        if (Array.isArray(routes)) {
          const match = routes.find(
            (r: any) => String(r?.id ?? r?.cuid ?? '') === String(selectedCuid),
          );
          if (match) {
            const candidate = match.pid ?? match.product_id ?? match.productId;
            if (isNumericPid(candidate)) return candidate;
          }
        }
      }
    } catch (err) {
    }

    // 2) apiService.products() — cuid→pid table
    const products = this.apiService.products() ?? [];
    if (products.length > 0) {
      const fromProducts = products.find(
        (p: any) => String(p?.id) === String(selectedCuid),
      );
      if (isNumericPid(fromProducts?.pid)) return fromProducts.pid;
    }

    // 3) selectedProductPid signal — last resort, may be stale
    const fromSignal = this.apiService.selectedProductPid();
    if (isNumericPid(fromSignal)) return fromSignal;

    return null;
  }

  private _syncCachedProductIdToSelectedProduct(
    selectedCuid: string | number | null,
  ): void {
    const resolvedPid = this.resolveActiveProductPid(selectedCuid);
    const nextValue: string | number | null = resolvedPid ?? selectedCuid;
 
    if (
      resolvedPid != null &&
      String(this.apiService.selectedProductPid() ?? '') !== String(resolvedPid)
    ) {
           this.apiService.setSelectedProductPid(resolvedPid);
    }

    if (
      nextValue != null &&
      String(this.cachedProductId() ?? '') !== String(nextValue)
    ) {
        this.cachedProductId.set(nextValue);
    }
  }
 
  override loadPersonalizedDashboardLayout(): void {
    const selectedCuid = this.apiService.selectedProductId();
    const isLoading = this.layoutLoading();
    const isMixed = this.isMixedDashboard();
       this._syncCachedProductIdToSelectedProduct(selectedCuid);

    if (isMixed && this.cachedProductId() == null) {

      this._pendingReloadForCuid.set(selectedCuid ?? 'pending');
      return;
    }

    if (isLoading) {

      this._pendingReloadForCuid.set(selectedCuid);
      return;
    }

    super.loadPersonalizedDashboardLayout();
  }
 
  /**
   * Reads the user's mixed ("My") dashboard for the given product and marks
   * every card on this page whose widget is already present & visible.
   * Delegates the actual HTTP call to {@link CustomDashboardPinService} so
   * the rehydration logic stays in lockstep with Monthly Report 1.
   */
  private hydrateAddedToCustomDashboardFromServer(productId: string | number): void {
    const selectedCuid = this.apiService.selectedProductId();
    const resolvedPid = this.resolveActiveProductPid(selectedCuid);
    if (resolvedPid != null && String(resolvedPid) !== String(productId)) {
      productId = resolvedPid;
    }

    this.customDashboardPin
      .hydrateAddedCardIds<MonthlyReport2SectionId>(
        this.dashboardEngineConfig.pageId,
        (key) => (isMonthlyReport2CardId(key) ? key : null),
        productId,
      )
      .subscribe((addedIds) => {
        if (!addedIds.size) return;
        this.addedToCustomDashboard.update((prev) => {
          const next = new Set(prev);
          for (const id of addedIds) next.add(id);
          return next;
        });
      });
  }
 
  private formatDateForApi(date: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return dayjs.tz(date, LA_TIMEZONE).format('YYYY-MM-DD');
    }
    return dayjs(date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
  }
 
  private seedInitialDateRangePayloads(): void {
    const start = this.startDate();
    const end = this.endDate();
    if (!start || !end) return;

    this.start_date = start;
    this.end_date = end;

    const payload = {
      start_time: this.formatDateForApi(start),
      end_time: this.formatDateForApi(end),
      unit: this.unit(),
      overall: 1,
    };

    const { previous_start, previous_end } = this.getPreviousPeriod(start, end);

    const previousPayload = {
      start_time: previous_start,
      end_time: previous_end,
      // Match the current-period payload's unit so the previous-period chart
      // is bucketed identically (was always re-derived from the range, which
      // ignored the user's explicit groupBy pick like Quarter / Year).
      unit: this.unit() || this.unitService.getUnitSuggestions(start, end).recommended,
      overall: 1,
    };

    this.dateRangePayloads.set({ payload, previousPayload });
  }

  ngOnInit(): void {
    // Layout is loaded by the product-selection effect above.
  }

  /* 
   * Backed by the engine's `layoutItems` signal — same API contract as before,
   * so the template's `@if (isVisible(SectionId.XXX))` pattern works unchanged.
   * Defaults to visible while the layout is still loading (items empty).
   */
  isVisible(sectionId: MonthlyReport2SectionId): boolean {
    const items = this.layoutItems();
    if (!items.length) return true; // show all sections until API responds
    return items.find(i => i.id === sectionId)?.visible ?? true;
  }

  // ─── Add to My Custom Dashboard (per-card quick action) ────────────────

  
  isAddedToCustomDashboard(card: DashboardCardLayoutItem<MonthlyReport2SectionId>): boolean {
    return this.addedToCustomDashboard().has(card.id);
  }

  /** True while the add-to-custom request for this specific card is in flight. */
  isAddingToCustomDashboard(card: DashboardCardLayoutItem<MonthlyReport2SectionId>): boolean {
    return this.addingToCustomDashboardId() === card.id;
  }

  /**
   * Add (or remove, when already pinned) a card on the user's mixed
   * ("Custom") dashboard via the shared {@link CustomDashboardPinService}.
   *
   * The helper centralises the existence probe + `PATCH /dashboards/<id>/
   * widgets/<wid>/visibility` (with `col_span` / `row_span` / `is_visible` /
   * `custom_config` payload) + first-time `POST /dashboards` mixed-seed
   * flow that previously lived inline here. Using the helper keeps the
   * visibility payload in sync with Monthly Report 1 (the page where this
   * pattern was first introduced) and avoids the legacy bug where the
   * inline path called `toggleVisibility(...)` with an empty body.
   */
  addToCustomDashboard(card: DashboardCardLayoutItem<MonthlyReport2SectionId>): void {
    if (!card || card.widgetId <= 0) return;
    if (this.addingToCustomDashboardId() !== null) return;

    const productId =
      this.cachedProductId() ??
      this.apiService.selectedProductPid() ??
      this.selectedProductId() ??
      this.apiService.selectedProductId() ??
      null;
    const resolvedColSpan =
      card.colSpan ?? this.SECTION_COL_SPAN[card.id] ?? 12;

    this.customDashboardPin.toggleLayoutItem({
      pageId: this.dashboardEngineConfig.pageId,
      widgetId: card.widgetId,
      sourcePageId: card.sourcePageId ?? this.dashboardEngineConfig.pageId,
      colSpan: resolvedColSpan,
      rowSpan: card.rowSpan ?? null,
      customConfig: card.customConfig ?? null,
      productId,
      isCurrentlyPinned: this.isAddedToCustomDashboard(card),
      onPinnedChange: (pinned) => {
        this.addedToCustomDashboard.update((s) => {
          const next = new Set(s);
          if (pinned) next.add(card.id);
          else next.delete(card.id);
          return next;
        });
      },
      onPendingChange: (pending) => {
        this.addingToCustomDashboardId.set(pending ? card.id : null);
      },
    });
  }

  // ─── Customize Layout editor (mini-dashboard preview) ──────────────────
   
  openCustomizeLayout(): void {
    const items = this.layoutItems()
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((item, idx) => {
        // Non-resizable cards mirror the static SECTION_COL_SPAN regardless of any stale col_span persisted in the API; resizable cards honour the API value so user resizes survive reopens.
        const staticSpan = this.SECTION_COL_SPAN[item.id] ?? 12;
        const colSpan = this.canResizeEditorCard(item.id)
          ? this._normalizeColSpan(item.colSpan ?? staticSpan)
          : this._normalizeColSpan(staticSpan);
        return {
          id: item.id,
          title: this.getCardTitle(item),
          visible: item.visible,
          colSpan,
          order: idx,
          widgetId: item.widgetId,
          placementId: item.placementId,
          sourcePageId: item.sourcePageId ?? this.dashboardEngineConfig.pageId,
          customConfig: item.customConfig ?? null,
        };
      });
    this.editorItems.set(items);
    this.editorMixedToggle.set(this.isMixedDashboard());
    // Snapshot the live dashboard layout so we can revert if the user cancels.
    this._layoutSnapshot = this.layoutItems().map((i) => ({ ...i }));
    this.isLayoutEditorOpen.set(true);
  }

  /** State for the inline Customize Layout editor. */
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);
  readonly editorMixedToggle = signal(false);

  /** Snapshot of layoutItems taken when the editor opens (used to revert on Cancel). */
  private _layoutSnapshot: ReturnType<typeof this.layoutItems> = [];

  /** Number of cards currently marked visible in the editor draft. */
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  /** Allowed col-span steps the user can cycle through per card. */
  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  /* All cards are resizable inside the Customize Layout editor — each tile exposes the full 4 / 6 / 8 / 12 width chip set so users can build any responsive arrangement. */
  canResizeEditorCard(_id: MonthlyReport2SectionId): boolean {
    return true;
  }

  /** Track-by for the editor cdkDropList. */
  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  /** Tailwind col-span class for a 12-col grid inside the editor preview. */
  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 3:  return 'col-span-3';
      case 4:  return 'col-span-4';
      case 6:  return 'col-span-6';
      case 8:  return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }
 
  isTallEditorTile(item: LayoutEditorItem): boolean {
    return (
      item.id === MonthlyReport2SectionId.TRAINING_STATUS &&
      this._normalizeColSpan(item.colSpan) === 4 &&
      this._shouldTrainingStatusSpanTwoRows(this.editorItems())
    );
  }
 
  private _shouldTrainingStatusSpanTwoRows(
    items: ReadonlyArray<{
      id: MonthlyReport2SectionId;
      visible: boolean;
      colSpan?: number | null;
    }> = this.layoutItems(),
  ): boolean {
    const find = (id: MonthlyReport2SectionId) =>
      items.find((i) => i.id === id);
    const isVisible = (id: MonthlyReport2SectionId): boolean =>
      find(id)?.visible ?? false;
    const spanOf = (id: MonthlyReport2SectionId): number => {
      const it = find(id);
      const raw = it?.colSpan ?? this.SECTION_COL_SPAN[id] ?? 12;
      return this._normalizeColSpan(raw);
    };

    // `lg:row-span-2` makes Training Status act as a tall sidebar next to
    // the wider Stats Overview + MoM Activity stack. It only makes sense
    // when both neighbours are actually wider than Training Status (col >
    // 4); otherwise (e.g. all three at col-span-4) the cards already fit
    // in a single row and the row-span would create an awkward tall card.
    return (
      isVisible(MonthlyReport2SectionId.STATS_OVERVIEW) &&
      isVisible(MonthlyReport2SectionId.MOM_ACTIVITY) &&
      spanOf(MonthlyReport2SectionId.STATS_OVERVIEW) > 4 &&
      spanOf(MonthlyReport2SectionId.MOM_ACTIVITY) > 4
    );
  }

  closeLayoutEditor(): void {
    // Revert any live visibility changes made via the eye toggle so Cancel 
    if (this._layoutSnapshot.length) {
      this.layoutItems.set(this._layoutSnapshot.map((i) => ({ ...i })));
    }
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorMixed(): void {
    this.editorMixedToggle.update((v) => !v);
  }

  toggleEditorCardVisibility(id: MonthlyReport2SectionId): void {
    // When a card is hidden, push it to the bottom of the list so the remaining visible cards reflow into the freed grid slot  
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );

    // Live-preview: mirror both the visibility change AND the new order onto
    // the actual dashboard so the live grid reflows immediately.
    this.layoutItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
  }

  /** Sets a specific col-span on a card (used by the inline width selector). Refuses values below the card's designed default. */
  setEditorCardColSpan(id: MonthlyReport2SectionId, span: number): void {
    const next = this._normalizeColSpan(span);
    if (this._isBelowDefaultColSpan(id, next)) return;
    this.editorItems.update((items) =>
      items.map((i) => (i.id === id ? { ...i, colSpan: next } : i)),
    );
  }

  /** Steps a card down one width (e.g. 12 → 8 → 6 → 4). Respects each card's designed default as the lower bound. */
  decreaseEditorCardColSpan(id: MonthlyReport2SectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
  }

  /** Steps a card up one width (e.g. 4 → 6 → 8 → 12). */
  increaseEditorCardColSpan(id: MonthlyReport2SectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
  }

  /**
   * Whether the minimize button should be active for this card.
   *
   * A card cannot shrink below its designed default col-span — e.g. a card
   * designed for `col-8` can only step between 8 and 12, never down to 6 or
   * 4. The button is disabled the moment the card reaches its default
   * width.
   */
  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
      return false;
    }
    return true;
  }

  /** Whether `span` is below the designed default col-span for `id`. */
  private _isBelowDefaultColSpan(id: MonthlyReport2SectionId, span: number): boolean {
    const defaultSpan = this.SECTION_COL_SPAN[id];
    if (defaultSpan == null) return false;
    return span < this._normalizeColSpan(defaultSpan);
  }

  /** Whether the maximize button should be active for this card. */
  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }
 
  /**
   * Auto-adjusts widths in the editor so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest card in each leaky row (and shares the
   * leftover equally when multiple cards tie at the smallest width). The
   * `isTallEditorTile` callback keeps Training Status' row-span-2 behaviour
   * honoured during the dense-pack simulation.
   */
  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(
      this.editorItems(),
      (i) => this.isTallEditorTile(i),
    );
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
  }

  /** Whether clicking Auto Adjust Width would actually grow at least one card. */
  canAutoAdjustEditorWidths(): boolean {
    return computeLayoutEditorAutoWidthUpdates(
      this.editorItems(),
      (i) => this.isTallEditorTile(i),
    ).size > 0;
  }

  /** Human-friendly label for a col-span value (Small / Medium / Large / Full). */
  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4:  return 'Small';
      case 6:  return 'Medium';
      case 8:  return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }

 /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  }

  /** Restores the editor draft to the page's default order, widths, and visibility. */
  resetLayoutEditorToDefaults(): void {
    const cardConfig = this.dashboardEngineConfig.cardConfig;
    const layoutById = new Map(this.layoutItems().map((i) => [i.id, i]));

    const defaults = [...cardConfig]
      .sort((a, b) => a.defaultOrder - b.defaultOrder)
      .map((cfg, idx) => {
        const existing = layoutById.get(cfg.id);
        return {
          id: cfg.id,
          title: cfg.title,
          visible: cfg.defaultVisible,
          colSpan: this._normalizeColSpan(this.SECTION_COL_SPAN[cfg.id] ?? 12),
          order: idx,
          widgetId: existing?.widgetId ?? 0,
          placementId: existing?.placementId,
          sourcePageId: existing?.sourcePageId ?? this.dashboardEngineConfig.pageId,
          customConfig: existing?.customConfig ?? null,
        };
      });

    this.editorItems.set(defaults);
  }

  /** Persists the editor draft. Updates local layout state immediately for a snappy UI, then writes through to the backend (when a dashboard exists)  the batch widgets endpoint.
   */
  applyLayoutEditor(): void {
    const draft = this.editorItems();

    const draftById = new Map(draft.map((d) => [d.id, d]));
    const updatedLocal = this.layoutItems()
      .map((item) => {
        const d = draftById.get(item.id);
        if (!d) return item;
        return {
          ...item,
          visible: d.visible,
          colSpan: d.colSpan,
          order: d.order,
        };
      })
      .sort((a, b) => a.order - b.order)
      .map((item, idx) => ({ ...item, order: idx }));

    this.layoutItems.set(updatedLocal);

    const dashId = this.dashboardId();

    // Send ALL widgets (both visible and hidden) to the backend with their
    // `is_visible` state so the server has the full layout snapshot.
    // Visibility is now controlled exclusively via the `is_visible` flag —
    // no widget is excluded from the payload.
    const allDraft = draft
      .filter((d) => d.widgetId > 0)
      .sort((a, b) => a.order - b.order);

    if (dashId && allDraft.length) {
      this._layoutService
        .batchUpdateWidgets(dashId, {
          widgets: allDraft.map((d) => ({
            widget_id: String(d.widgetId),
            source_page_id: String(
              d.sourcePageId ?? this.dashboardEngineConfig.pageId,
            ),
            col_span: d.colSpan,
            row_span: 1,
            is_visible: d.visible,
            custom_config: d.customConfig ?? {},
          })),
        })
        .pipe(catchError(() => of(null)))
        .subscribe(() => this.loadPersonalizedDashboardLayout());
    }

    if (this.editorMixedToggle() !== this.isMixedDashboard()) {
      this.isMixedDashboard.set(this.editorMixedToggle());
    }

    this._layoutSnapshot = [];
    this.isLayoutEditorOpen.set(false);
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [3, 4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  /** Static literal classes so Tailwind JIT keeps them in the bundle. */
  private _dynamicColClass(span: number): string {
    switch (span) {
      case 4:  return 'col-span-12 lg:col-span-4';
      case 6:  return 'col-span-12 lg:col-span-6';
      case 8:  return 'col-span-12 lg:col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  private readonly SECTION_COL_CLASSES: Partial<Record<MonthlyReport2SectionId, string>> = {
    [MonthlyReport2SectionId.STATS_OVERVIEW]:    'col-span-12 lg:col-span-8',
    [MonthlyReport2SectionId.MOM_ACTIVITY]:      'col-span-12 lg:col-span-8',
    [MonthlyReport2SectionId.TRAINING_STATUS]:   'col-span-12 lg:col-span-4 lg:row-span-2',
    [MonthlyReport2SectionId.MONTHLY_ANALYSIS]:  'col-span-12',
    [MonthlyReport2SectionId.ENROLLED_RUNNING]:  'col-span-12 lg:col-span-6',
    [MonthlyReport2SectionId.CONTRACTS_RUNNING]: 'col-span-12 lg:col-span-6',
  };

  private readonly SECTION_COL_SPAN: Partial<Record<MonthlyReport2SectionId, number>> = {
    [MonthlyReport2SectionId.STATS_OVERVIEW]:    8,
    [MonthlyReport2SectionId.MOM_ACTIVITY]:      8,
    [MonthlyReport2SectionId.TRAINING_STATUS]:   4,
    [MonthlyReport2SectionId.MONTHLY_ANALYSIS]:  12,
    [MonthlyReport2SectionId.ENROLLED_RUNNING]:  6,
    [MonthlyReport2SectionId.CONTRACTS_RUNNING]: 6,
  };

  getSectionColClass(card: { id: MonthlyReport2SectionId; colSpan?: number | null } | MonthlyReport2SectionId): string {
    const id = typeof card === 'string' ? card : card.id;
    const dynamicSpan = typeof card === 'string' ? null : (card.colSpan ?? null);

    const staticClass = this.SECTION_COL_CLASSES[id] ?? 'col-span-12';
    const staticSpan = this.SECTION_COL_SPAN[id] ?? 12;

    // Embedded mode (my-dashboard / customize-state): always purely col-span
    // so the rendered grid mirrors the customize-dialog preview exactly.
    if (this.mixedMode()) {
      const span = this._normalizeColSpan(dynamicSpan ?? staticSpan);
      return this._dynamicColClass(span);
    }

    if (
      this.canResizeEditorCard(id) &&
      dynamicSpan != null &&
      this._normalizeColSpan(dynamicSpan) !== staticSpan
    ) {
      return this._dynamicColClass(this._normalizeColSpan(dynamicSpan));
    }

    if (id === MonthlyReport2SectionId.TRAINING_STATUS) {
      const base = 'col-span-12 lg:col-span-4';
      return this._shouldTrainingStatusSpanTwoRows()
        ? `${base} lg:row-span-2`
        : base;
    }

    return staticClass;
  }

  unit = signal('day');
  businessTrainingStatsData: any[] = [];
  businessContractsMomActivityData: any = {
    contractsCreatedData: [],
    previousContractsCreatedData: [],
    registeredBusinessData: [],
    previousRegisteredBusinessData: [],
  };
  businessContractsOverallStats: any[] = [];
  businessEnrolledRunningTotalData: any[] = [];
  previousBusinessEnrolledRunningTotalData: any[] = [];
  contractsCreatedRunningTotalData: any[] = [];
  previousContractsCreatedRunningTotalData: any[] = [];
  start_date: any;
  end_date: any;
  currentMonthStatsCards: any = [];
  monthlyActivityAnalysis: any[] = [];
  monthlyActivityAnalysisHeaders: any[] = [];
  tabOptions = [
    {
      label: 'Line Chart',
      tooltip: 'Click to View Line Graph',
      isTable: false,
      value: 'lineChart',
      iconName: 'chartLine' as IconName,
    },
    {
      label: 'Bar Chart',
      tooltip: 'Click to View Bar Graph',
      isTable: true,
      value: 'barChart',
      iconName: 'chartColumn' as IconName,
    },
  ];

  iconBgColors = [
    'bg-[#2B7FFF]',
    'bg-[#AD46FF]',
    'bg-[#F6339A]',
    'bg-[#615FFF]',
    'bg-[#2B7FFF]',
    'bg-[#AD46FF]',
    'bg-[#F6339A]',
    'bg-[#615FFF]',
  ];

  iconTextColors = [
    'text-[#2B7FFF]',
    'text-[#AD46FF]',
    'text-[#F6339A]',
    'text-[#615FFF]',
    'text-[#2B7FFF]',
    'text-[#AD46FF]',
    'text-[#F6339A]',
    'text-[#615FFF]',
  ];

  colors: any = {
    // data1: '#a78bfa' as `#${string}` // blue-500 for bars
    data1: '#ac46ffd5' as `#${string}`, // blue-500 for bars
  };

  private viewSignals: Record<
    string,
    WritableSignal<'lineChart' | 'barChart'>
  > = {
      momActivity: signal('lineChart'),
      enrolledBusinesses: signal('lineChart'),
      enrolledContracts: signal('lineChart'),
    };
  getView(chartKey: string) {
    return this.viewSignals[chartKey]();
  }

  setActiveTab(tab: any, chartKey: string): void {
    this.viewSignals[chartKey].set(tab);
  }
  // Current timestamp for display
  get currentTimestamp(): string {
    return new Date().toLocaleString();
  }

  // Generate unique issue ID
  get issueId(): string {
    return `MR2-${Date.now()}`;
  }

  rangeValues(event: any) {

  }

  private getPreviousPeriod(startDate: string, endDate: string) {
    const start = dayjs.tz(startDate, LA_TIMEZONE).startOf('day');
    const end = dayjs.tz(endDate, LA_TIMEZONE).endOf('day');
    const durationMs = end.valueOf() - start.valueOf();

    const prevEnd = start.subtract(1, 'millisecond');
    const prevStart = dayjs(prevEnd.valueOf() - durationMs);

    return {
      previous_start: prevStart.tz(LA_TIMEZONE).format('YYYY-MM-DD'),
      previous_end: prevEnd.tz(LA_TIMEZONE).format('YYYY-MM-DD'),
    };
  }

  isPrevious: boolean = false;

  /** Controls the "Add hidden section" popover in the toolbar. */
  isAddSectionMenuOpen = signal(false);

  toggleAddSectionMenu(): void {
    this.isAddSectionMenuOpen.update(v => !v);
  }

  onDateRangeChange(event: DateRangePickerOutput) {

    // event contains: start_date, end_date, previous_start_date, previous_end_date, unit

    // Example usage:
    const {
      start_date,
      end_date,
      previous_start_date,
      previous_end_date,
      unit,
      comparison_type,
      preset_label,
    } = event;

    this.isPrevious = !!(previous_start_date && previous_end_date);

    const apiStart = this.formatDateForApi(start_date);
    const apiEnd = this.formatDateForApi(end_date);

    this.startDate.set(this.isPrevious && previous_start_date
      ? this.formatDateForApi(previous_start_date)
      : apiStart);
    this.endDate.set(this.isPrevious && previous_end_date
      ? this.formatDateForApi(previous_end_date)
      : apiEnd);
 
    this.currentStartDate.set(apiStart);
    this.currentEndDate.set(apiEnd);
    if (comparison_type) {
      this.comparisonType.set(comparison_type);
    }
    this.presetLabel.set(preset_label);

    this.start_date = apiStart;
    this.end_date = apiEnd;
    this.unit.set(unit);
    let payload = {
      start_time: apiStart,
      end_time: apiEnd,
      unit: this.unit(),
      overall: 1,
    };

    const { previous_start, previous_end } = this.getPreviousPeriod(apiStart, apiEnd);

    /** PREVIOUS PAYLOAD */
    const previousPayload = {
      start_time: previous_start,
      end_time: previous_end,
      // Mirror the current-period unit (set above from the picker emission)
      // so the comparison series is bucketed at the same granularity the
      // user selected via groupBy.
      unit: this.unit() || this.unitService.getUnitSuggestions(apiStart, apiEnd).recommended,
      overall: 1,
    };

    this.dateRangePayloads.set({ payload, previousPayload });
    // Use the data for API calls or other logic
    // if (previous_start_date && previous_end_date) {

    // } else {

    // }
  }

  getKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  openFilterSheet() { 
    const effectiveStart = this.currentStartDate() ?? this.startDate();
    const effectiveEnd = this.currentEndDate() ?? this.endDate();
    const startStr = this.datePipe.transform(effectiveStart, 'MMM dd, yyyy');
    const endStr = this.datePipe.transform(effectiveEnd, 'MMM dd, yyyy');
    const dateSubtitle = startStr && endStr ? `${startStr} - ${endStr}` : '';

    const sheetRef = this._bottomSheet.open(MobileSheetComponent, {
      panelClass: 'mobile-sheet-wrapper-container',
      data: {
        title: 'Filter',
        subtitle: dateSubtitle,
        icon: 'calendar',
        component: DatePickerComponent,
        data: {
          isMobileMode: true, 
          value: {
            startDate: effectiveStart,
            endDate: effectiveEnd,
            unit: this.unit(),
            comparisonType: this.comparisonType(),
            presetLabel: this.presetLabel(),
          },
        },
      } as MobileSheetData,
    });

    sheetRef.afterDismissed().subscribe((result: DateRangePickerOutput) => {
      if (result) {
        this.onDateRangeChange(result);
      }
    });
  }
 
  private ensureDefaultDateRangeForMixedMode(): void {
    if (this.start_date && this.end_date) return;

    const end = dayjs.utc().tz(LA_TIMEZONE);
    const start = end.subtract(29, 'day');

    const startStr = start.format('YYYY-MM-DD');
    const endStr = end.format('YYYY-MM-DD');

    this.start_date = startStr;
    this.end_date = endStr;
    this.unit.set('day');
    this.isPrevious = false;

    const payload = {
      start_time: startStr,
      end_time: endStr,
      unit: this.unit(),
      overall: 1,
    };

    // Previous payload is required by the `fetchAllReports` signature but
    // will be ignored at runtime because `isPrevious === false` short-circuits
    // every `usePrevious` endpoint inside the loop.
    const { previous_start, previous_end } = this.getPreviousPeriod(
      startStr,
      endStr,
    );
    const previousPayload = {
      start_time: previous_start,
      end_time: previous_end,
      unit: this.unit(),
      overall: 1,
    };

    this.fetchAllReports(payload, previousPayload);
  }

  private fetchAllReports(payload: any, previousPayload: any): void {
    const productId = this.selectedProductId();
    if (!productId) return;

    const productName = this.productConfigService.getProductName(productId);
    const productConfig = this.productConfigService.getProductConfig(productName);
    const fetchGeneration = this.reportFetchGeneration;
    const fetchProductId = productId;

    // this.productConfigService.ensureValidToken(this.selectedProductId()).subscribe({
    //   next: () => {
        const options = {
          showErrorToaster: true,
          showSuccessToaster: false,
          showLoader: false,
          cache: true,
          addAccessToken: productConfig.requiresAccessToken,
          productServerName: productConfig.productServerName,
        };

        const endpoints: Array<{
          key: string;
          url: string;
          handler: (res: any) => void;
          usePrevious?: boolean;
          sections: MonthlyReport2SectionId[];
        }> = [
          { key: 'businessTraining', url: '/business_training_stats', handler: this.handleBusinessTrainingStats, sections: [MonthlyReport2SectionId.STATS_OVERVIEW, MonthlyReport2SectionId.TRAINING_STATUS] },
          { key: 'contractsOverall', url: '/business_contracts_overall', handler: this.handleBusinessContractsOverall, sections: [MonthlyReport2SectionId.STATS_OVERVIEW] },
          { key: 'contractsOverall', url: '/business_contracts_overall', usePrevious: true, handler: this.handlePreviousBusinessContractsOverall, sections: [MonthlyReport2SectionId.STATS_OVERVIEW] },
          { key: 'momActivity', url: '/business_contracts_mom_activity', handler: this.handleBusinessContractsMomActivity, sections: [MonthlyReport2SectionId.MOM_ACTIVITY, MonthlyReport2SectionId.MONTHLY_ANALYSIS] },
          { key: 'momActivity', url: '/business_contracts_mom_activity', usePrevious: true, handler: this.handlePreviousBusinessContractsMomActivity, sections: [MonthlyReport2SectionId.MOM_ACTIVITY, MonthlyReport2SectionId.MONTHLY_ANALYSIS] },
          { key: 'enrolledRunning', url: '/business_enrolled_running_total', handler: this.handleBusinessEnrolledRunningTotal, sections: [MonthlyReport2SectionId.ENROLLED_RUNNING] },
          { key: 'enrolledRunning', url: '/business_enrolled_running_total', usePrevious: true, handler: this.handlePreviousBusinessEnrolledRunningTotal, sections: [MonthlyReport2SectionId.ENROLLED_RUNNING] },
          { key: 'contractsRunning', url: '/contracts_created_running_total', handler: this.handleContractsCreatedRunningTotal, sections: [MonthlyReport2SectionId.CONTRACTS_RUNNING] },
          { key: 'contractsRunning', url: '/contracts_created_running_total', usePrevious: true, handler: this.handlePreviousContractsCreatedRunningTotal, sections: [MonthlyReport2SectionId.CONTRACTS_RUNNING] },
        ];

        const toolPath = productConfig.reportToolsPath || 'visionboard/tools';

        for (let { key, url, handler, usePrevious, sections } of endpoints) {
          if (usePrevious && !this.isPrevious) continue;
          if (!this.isSectionActive(...sections)) continue;

          const finalPayload = usePrevious ? previousPayload : payload;
          this.setSectionLoading(key, true);
          this.apiService.post(`${toolPath}${url}`, finalPayload, options).subscribe({
            next: (res) => {
              const isStale = fetchGeneration !== this.reportFetchGeneration
                || fetchProductId !== this.selectedProductId();
              if (isStale) {
                this.setSectionLoading(key, false);
                return;
              }
              handler.call(this, res);
              this.setSectionLoading(key, false);
            },
            error: (err) => {
              this.setSectionLoading(key, false);
            },
          });
        } 
  }

  private handleBusinessTrainingStats(res: any) {
    this.businessTrainingStatsData = res.data.training_statuses.map(
      (item: any, index: number) => ({
        status: item.status_name,
        count: item.count,
      })
    );
    this.currentMonthStatsCards = [
      {
        label: 'Trainings',
        key: 'trainings',
        icon: 'users',
        value: res.data.total_trainings,
        bgClass: 'bg-blue-50 dark:bg-blue-900/80',
        iconClass: 'text-blue-600 dark:text-blue-400',
      },
      {
        label: 'Enrolled',
        key: 'enrolled',
        icon: 'target',
        value: res.data.total_enrolled_doctors,
        bgClass: 'bg-green-50 dark:bg-green-900/80',
        iconClass: 'text-green-600 dark:text-green-400',
      },
      {
        label: 'Contracts',
        key: 'contracts',
        icon: 'fileText',
        value: res.data.total_financed_contracts,
        bgClass: 'bg-purple-50 dark:bg-purple-900/80',
        iconClass: 'text-purple-600 dark:text-purple-400',
      },
    ];
  }

 

  private buildBusinessContractsOverallStats() {
    // We need at least current data
    if (!this.currentBusinessContractsOverall) return;

    const current = this.currentBusinessContractsOverall;
    const previous = this.previousBusinessContractsOverall; // can be null

    const buildItem = (
      label: string,
      key: string,
      icon: IconName,
      bgClass: string
    ) => {
      const rawCurrent = current[key];
      const rawPrevious = previous ? previous[key] : null;

      const currValueNum = rawCurrent != null ? Number(rawCurrent) : null;
      const prevValueNum = rawPrevious != null ? Number(rawPrevious) : null;

      let difference: number | null = null;
      let percentChange: number | null = null;
      let trend: 'up' | 'down' | 'same' | null = null;

      if (
        prevValueNum != null &&
        !isNaN(prevValueNum) &&
        prevValueNum !== 0 &&
        currValueNum != null &&
        !isNaN(currValueNum)
      ) {
        difference = currValueNum - prevValueNum;
        percentChange = (difference / prevValueNum) * 100;
        trend = difference > 0 ? 'up' : difference < 0 ? 'down' : 'same';
      }

      return {
        label,
        key,
        icon,
        value: rawCurrent, // keep original value for display ("194.12")
        previousValue: rawPrevious, // raw previous value

        difference: difference !== null ? difference.toFixed(1) : null, // number | null
        percentChange, // number | null
        trend, // 'up' | 'down' | 'same' | null

        bgClass,
        iconClass: 'text-[#FFFFFF] dark:text-surface-1',
      };
    };

    this.businessContractsOverallStats = [
      buildItem(
        'Business Enrolled',
        'total_enrolled',
        'users',
        'bg-[#3B82F6] dark:bg-[#3B82F6]'
      ),
      buildItem(
        'Businesseses With Contract',
        'businesses',
        'fileText',
        'bg-[#7C3AED] dark:bg-[#7C3AED]'
      ),
      buildItem(
        'Contracts',
        'total_contracts',
        'building2',
        'bg-[#FF6900] dark:bg-[#FF6900]'
      ),
      buildItem(
        'Activation Rate',
        'activation_rate',
        'trendingUp',
        'bg-[#15B268] dark:bg-[#15B268]'
      ),
    ];
  }

  public previousBusinessContractsOverall: any = null;
  public currentBusinessContractsOverall: any = null;

  private handlePreviousBusinessContractsOverall(res: any) {
    this.previousBusinessContractsOverall = {
      total_enrolled: res.data.businesses_with_contracts,
      businesses: res.data.total_businesses,
      total_contracts: res.data.total_contracts,
      activation_rate: res.data.activation_rate_pct,
    };

    this.buildBusinessContractsOverallStats();
  }

  private handleBusinessContractsOverall(res: any) {
    this.currentBusinessContractsOverall = {
      total_enrolled: res.data.businesses_with_contracts,
      businesses: res.data.total_businesses,
      total_contracts: res.data.total_contracts,
      activation_rate: res.data.activation_rate_pct,
    };

    this.buildBusinessContractsOverallStats();
  }

  private handleBusinessContractsMomActivity(res: any) {
    this.businessContractsMomActivityData.contractsCreatedData = [];

    this.businessContractsMomActivityData.registeredBusinessData = [];

    this.monthlyActivityAnalysis = [];

    res.data.forEach((d: any) => {
      this.businessContractsMomActivityData.contractsCreatedData.push({
        key: 'Contract Created',
        value: d.total_contracts_created,
        start: d.start,
        end: d.end,
        label: this.formatDateRangeByUnit(d?.start, d?.end, this.unit()),
      });

      this.businessContractsMomActivityData.registeredBusinessData.push({
        key: 'Registered Business',
        value: d.total_registered,
        start: d.start,
        end: d.end,
        label: this.formatDateRangeByUnit(d?.start, d?.end, this.unit()),
      });
      this.monthlyActivityAnalysis.push({
        [this.unit()]: this.formatStartDate(d.start, d?.end, this.unit()),
        'total registered(mom)': d.total_registered,
        'total contracts created(mom)': d.total_contracts_created,
        'new businesses contract created': d.new_doctors_with_contracts,
        'new businesses activation rate(%)': d.new_business_activation_rate,
      });
    });


  }

  private handlePreviousBusinessContractsMomActivity(res: any) {
    if (!this.isPrevious) {
      this.businessContractsMomActivityData.previousContractsCreatedData = [];
      this.businessContractsMomActivityData.previousRegisteredBusinessData = [];
      return;
    }

    res.data.forEach((d: any) => {
      this.businessContractsMomActivityData.previousContractsCreatedData.push({
        key: 'Previous Contract Created',
        value: d.total_contracts_created,
        start: d.start,
        end: d.end,
        label: this.formatDateRangeByUnit(d?.start, d?.end, this.unit()),
      });

      this.businessContractsMomActivityData.previousRegisteredBusinessData.push(
        {
          key: 'Previous Registered Business',
          value: d.total_registered,
          start: d.start,
          end: d.end,
          label: this.formatDateRangeByUnit(d?.start, d?.end, this.unit()),
        }
      );
    });


  }

  private formatStartDate(
    startDate: string,
    endDate: string,
    unit: string
  ): string {
    if (unit === 'week') {
      return `${dayjs(startDate).format('DD MMM')} - ${dayjs(endDate).format(
        'DD MMM'
      )}`;
    } else if (unit === 'day') {
      return `${dayjs(startDate).format('DD MMM')}`;
    } else if (unit === 'quarter') {
      return `${dayjs(startDate).format('MMM YY')} - ${dayjs(endDate).format(
        'MMM YY'
      )}`;
    } else if (unit === 'year') {
      return `${dayjs(startDate).format('YYYY')}`;
    } else if (unit === 'month') {
      return `${dayjs(startDate).format('MMM YYYY')}`;
    } else if (unit === 'hour') {
      return `${dayjs(startDate).format('DD MMM HH:mm')}`;
    }
    return dayjs(startDate).format('DD MMM');
  }

  private formatDateRangeByUnit(
    startDate: string,
    endDate: string,
    unit: string
  ): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    switch (unit) {
      case 'hour':
        // For hourly: show date with hour range
        if (start.isSame(end, 'day')) {
          return `${start.format('DD MMM')} (${start.format(
            'HH:mm'
          )} - ${end.format('HH:mm')})`;
        }
        return `${start.format('DD MMM HH:mm')} - ${end.format(
          'DD MMM HH:mm'
        )}`;

      case 'day':
        // For daily: show date range
        if (start.isSame(end, 'month')) {
          return `${start.format('DD MMM YYYY')}`;
        }
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;

      case 'week':
        // For weekly: show date range
        return `${start.format('DD')} - ${end.format('DD MMM YYYY')}`;

      case 'month':
        // For monthly: show month range
        if (start.isSame(end, 'year')) {
          return `${start.format('MMM YYYY')}`;
        }
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;

      case 'quarter':
        // For quarterly: show quarter range
        return `Q${start.quarter()} ${start.format(
          'YYYY'
        )} - Q${end.quarter()} ${end.format('YYYY')}`;

      case 'year':
        // For yearly: show year range
        if (start.year() === end.year()) {
          return start.format('YYYY');
        }
        return `${start.format('YYYY')} - ${end.format('YYYY')}`;

      default:
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
    }
  }

  private handleBusinessEnrolledRunningTotal(res: any) {
    let maxTotal = 0;
    this.businessEnrolledRunningTotalData = res.data.map((d: any) => {
      maxTotal = Math.max(maxTotal, d.running_total_enrollments || 0);
      return {
        value: maxTotal,
        start: d.start,
        end: d.end,
        key: 'Businesses Enrolled',
        label: this.formatDateRangeByUnit(
          d?.start,
          d?.end,
          this.unit()
        ),
      };
    });
  }

  private handlePreviousBusinessEnrolledRunningTotal(res: any) {
    if (!this.isPrevious) {
      this.previousBusinessEnrolledRunningTotalData = [];
      return;
    }

    let maxTotal = 0;
    this.previousBusinessEnrolledRunningTotalData = res.data.map((d: any) => {
      maxTotal = Math.max(maxTotal, d.running_total_enrollments || 0);
      return {
        value: maxTotal,
        start: d.start,
        end: d.end,
        key: 'Previous Businesses Enrolled',
        label: this.formatDateRangeByUnit(d.start, d.end, this.unit()),
      };
    });


  }

  private handleContractsCreatedRunningTotal(res: any) {
    let maxTotal = 0;
    this.contractsCreatedRunningTotalData = res.data.map((d: any) => {
      maxTotal = Math.max(maxTotal, d.running_total_contracts || 0);
      return {
        value: maxTotal,
        start: d.start,
        end: d.end,
        key: 'Contracts Created',
        label: this.formatDateRangeByUnit(
          d?.start,
          d?.end,
          this.unit()
        ),
      };
    });
  }
  private handlePreviousContractsCreatedRunningTotal(res: any) {
    if (!this.isPrevious) {
      this.previousContractsCreatedRunningTotalData = [];
      return;
    }
    let maxTotal = 0;
    this.previousContractsCreatedRunningTotalData = res.data.map((d: any) => {
      maxTotal = Math.max(maxTotal, d.running_total_contracts || 0);
      return {
        value: maxTotal,
        start: d.start,
        end: d.end,
        key: 'Previous Contracts Created',
        label: this.formatDateRangeByUnit(
          d?.start,
          d?.end,
          this.unit()
        ),
      };
    });
  }

  iconsName = ['calendar', 'users', 'fileText', 'trendingUp', 'trendingUp'];
 
  createTableHeaders(data: any[]): TableHeader[] {
    const unit = this.unit();
    const dateLabel = this.getDateLabel(unit);

    if (!data || !data.length) return [];
    const firstItem = data[0];
    return Object.keys(firstItem).map((key, index) => {
      let align: 'text-left' | 'text-center' | 'text-right';

      if (index === 0) align = 'text-left'; // 1st column
      else if (index === firstItem.length - 1)
        align = 'text-right'; // Last column
      else align = 'text-center';

      return {
        label: this.formatHeaderLabel(key),
        key,
        align,
        icon: (this.iconsName[index] as IconName) || undefined,
      };
    }) as TableHeader[];
  }

  private getDateLabel(unit: string): string {
    const labels: { [key: string]: string } = {
      hour: 'Hour',
      day: 'Date',
      week: 'Week',
      month: 'Month',
      quarter: 'Quarter',
      year: 'Year',
    };
    return labels[unit] || 'Date';
  }

  private formatHeaderLabel(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  getBgColor(i: number) {
    return this.iconBgColors[i % this.iconBgColors.length];
  }

  getTextColor(i: number) {
    return this.iconTextColors[i % this.iconTextColors.length];
  }

  getTotalCount() {
    return this.businessTrainingStatsData.reduce(
      (acc, item) => acc + item.count,
      0
    );
  }

  get toolTipData() {
    return [
      {
        label: this.isPrevious
          ? 'Previous Contract Created:'
          : 'Contract Created:',
        key: 'contractCreated',
      },
      {
        label: this.isPrevious
          ? 'Previous Registered Businesses:'
          : 'Registered Businesses:',
        key: 'registeredBusinesses',
      },
    ];
  }
  get linetoolTipData() {
    return [
      {
        isAmount: true,
        label: this.isPrevious
          ? 'Previous Contract Created:'
          : 'Contract Created:',
        key: 'contractCreated',
      },
      {
        isAmount: true,
        label: this.isPrevious
          ? 'Previous Registered Businesses:'
          : 'Registered Businesses:',
        key: 'registeredBusinesses',
      },
    ];
  }
}
